import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Search,
  Plus,
  Check,
  Clock,
  MessageSquare,
  CheckCheck,
  Users,
  Eye,
  EyeOff,
  ArrowDownAZ,
  ArrowDownWideNarrow,
  Filter,
  FilterX,
  ListChecks,
  ChevronDown,
  Inbox,
  CheckSquare,
} from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import useWhatsApps from "../../hooks/useWhatsApps";
import ConfirmationModal from "../ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Botão de ferramenta da lista: só o ícone, com o nome aparecendo embaixo no
// hover — como na referência, onde o rótulo surge quando a mão chega.
const FerramentaDaLista = ({
  icone: Icone,
  rotulo,
  ativo,
  onClick,
  classe,
}) => (
  <div className="group/ferramenta relative">
    <button
      type="button"
      aria-label={rotulo}
      aria-pressed={ativo}
      onClick={onClick}
      className={cn(
        "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
        ativo
          ? "bg-primary/10 text-primary"
          : cn(
              "text-muted-foreground hover:bg-muted hover:text-foreground",
              classe,
            ),
      )}
    >
      <Icone className="h-[18px] w-[18px]" />
    </button>

    <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-0.5 -translate-x-1/2 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[10px] font-medium text-background opacity-0 transition-opacity group-hover/ferramenta:opacity-100">
      {rotulo}
    </span>
  </div>
);

// Filtro de várias escolhas. Encapsula o padrão "rótulo + quantos escolhidos
// + lista com caixas de seleção", que se repete nos três filtros.
const FiltroDeLista = ({ rotulo, opcoes, selecionados, onChange }) => {
  const alternar = (id) =>
    onChange(
      selecionados.includes(id)
        ? selecionados.filter((s) => s !== id)
        : [...selecionados, id],
    );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-8 w-full items-center gap-1.5 rounded-md border px-2.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {rotulo}
          {selecionados.length > 0 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold tabular-nums text-primary-foreground">
              {selecionados.length}
            </span>
          )}
          <ChevronDown className="ml-auto h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="scrollbar-discreta max-h-72 w-64 overflow-y-auto p-1"
      >
        {(opcoes || []).length === 0 ? (
          <p className="p-3 text-xs text-muted-foreground">
            Nada para filtrar.
          </p>
        ) : (
          opcoes.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => alternar(o.id)}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-muted"
            >
              <span
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                  selecionados.includes(o.id)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input",
                )}
              >
                {selecionados.includes(o.id) && <Check className="h-3 w-3" />}
              </span>
              {o.color && (
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: o.color }}
                />
              )}
              <span className="truncate">{o.name}</span>
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
};

// Um estado por aba, nomeado pelo que a pessoa precisa fazer — não pelo
// status interno do ticket.
// Abas do painel "Abertos", na ordem da referência. Resolvidos não é aba:
// é o outro painel, alcançado pelos botões Abertos/Fechados da barra.
const ESTADOS = [
  {
    value: "open",
    rotulo: "Atendendo",
    icone: MessageSquare,
    ajuda: "Conversas em andamento",
    vazio: {
      titulo: "Nenhum atendimento em andamento",
      texto: "Aceite uma conversa em Aguardando para começar.",
    },
  },
  {
    value: "pending",
    rotulo: "Aguardando",
    icone: Clock,
    ajuda: "Ninguém assumiu ainda",
    vazio: {
      titulo: "Nenhum cliente esperando",
      texto: "Novas conversas aparecem aqui assim que chegam.",
    },
  },
  {
    value: "grupos",
    rotulo: "Grupos",
    icone: Users,
    ajuda: "Conversas em grupos do WhatsApp",
    somenteGrupos: true,
    vazio: {
      titulo: "Nenhuma conversa em grupo",
      texto: "Grupos aparecem aqui quando alguém escreve neles.",
    },
  },
];

const RESOLVIDOS = {
  value: "closed",
  rotulo: "Resolvidos",
  icone: CheckCheck,
  ajuda: "Atendimentos encerrados",
  vazio: {
    titulo: "Nada resolvido por aqui",
    texto: "Os atendimentos encerrados ficam guardados nesta aba.",
  },
};

const TicketsManager = () => {
  const [searchParam, setSearchParam] = useState("");
  // Com a busca sempre na tela, "estar buscando" é ter texto digitado —
  // não um modo que se liga e desliga.
  const buscando = searchParam.trim().length > 0;
  const [estado, setEstado] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);

  const [contagens, setContagens] = useState({
    pending: 0,
    open: 0,
    grupos: 0,
  });
  const { whatsApps } = useWhatsApps();

  // A aba de grupos só existe se alguma conexão estiver configurada para
  // atendê-los — do contrário é uma aba sempre vazia.
  const mostrarGrupos = (whatsApps || []).some((w) => w.showGroups);
  const abas = ESTADOS.filter((e) => !e.somenteGrupos || mostrarGrupos);

  // Se a aba sumir (conexão desmarcada), volta para uma aba que existe.
  useEffect(() => {
    if (estado === "grupos" && !mostrarGrupos) setEstado("open");
  }, [estado, mostrarGrupos]);

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);

  // "Todos" começa desligado para todo mundo, inclusive administrador: a
  // lista abre com os atendimentos de quem entrou, e ver os dos outros é uma
  // escolha de quem clica no botão. Antes o admin caía direto na fila
  // inteira da empresa e não tinha como enxergar só o que era dele.

  // "abertos" mostra as abas Atendendo/Aguardando/Grupos; "fechados" mostra
  // só os resolvidos, como na referência.
  const [painel, setPainel] = useState("abertos");
  const [ordemAZ, setOrdemAZ] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [filtroTags, setFiltroTags] = useState([]);
  const [filtroUsuarios, setFiltroUsuarios] = useState([]);
  const [filtroConexoes, setFiltroConexoes] = useState([]);
  const [etiquetas, setEtiquetas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  // As opções dos filtros só são buscadas quando os filtros aparecem: quem
  // nunca abre a gaveta não paga por ela.
  useEffect(() => {
    if (!filtrosAbertos || etiquetas.length || usuarios.length) return;

    (async () => {
      try {
        const [t, u] = await Promise.all([api.get("/tags"), api.get("/users")]);
        setEtiquetas(t.data?.tags || t.data || []);
        setUsuarios(u.data?.users || []);
      } catch (err) {
        // Sem as listas os filtros abrem vazios; não vale derrubar a tela.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosAbertos]);
  const [confirmarFecharTodos, setConfirmarFecharTodos] = useState(false);

  const fecharTodos = async () => {
    try {
      const { data } = await api.put("/tickets/close-all");
      toast.success(
        data?.encerrados
          ? `${data.encerrados} atendimento(s) encerrado(s).`
          : "Nenhum atendimento em andamento.",
      );
    } catch (err) {
      toastError(err);
    }
  };

  let searchTimeout;

  const handleSearch = (e) => {
    const termo = e.target.value.toLowerCase();
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => setSearchParam(termo), 500);
  };

  const atualizarContagem = (chave) => (valor) =>
    setContagens((anterior) =>
      anterior[chave] === valor ? anterior : { ...anterior, [chave]: valor },
    );

  const estadoAtual = ESTADOS.find((e) => e.value === estado);

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-background">
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
      />

      {/* Busca, com o interruptor que amplia a procura para o conteúdo das
          mensagens — é mais lenta, então fica desligada por padrão. */}
      <div className="flex shrink-0 items-center gap-2 px-3 pb-1.5 pt-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Buscar atendimento e mensagens"
            type="search"
            onChange={handleSearch}
            className="h-9 rounded-full pl-9"
          />
        </div>

        <button
          type="button"
          aria-label={filtrosAbertos ? "Esconder filtros" : "Mostrar filtros"}
          title={filtrosAbertos ? "Esconder filtros" : "Mostrar filtros"}
          aria-pressed={filtrosAbertos}
          onClick={() => setFiltrosAbertos((v) => !v)}
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors",
            filtrosAbertos
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          {filtrosAbertos ? (
            <Filter className="h-4 w-4" />
          ) : (
            <FilterX className="h-4 w-4" />
          )}
        </button>
      </div>

      {filtrosAbertos && (
        <div className="shrink-0 space-y-2 border-b px-3 pb-3">
          <FiltroDeLista
            rotulo="Etiquetas"
            opcoes={etiquetas}
            selecionados={filtroTags}
            onChange={setFiltroTags}
          />
          <FiltroDeLista
            rotulo="Conexões"
            opcoes={whatsApps}
            selecionados={filtroConexoes}
            onChange={setFiltroConexoes}
          />
          <Can
            role={user.profile}
            perform="tickets-manager:showall"
            yes={() => (
              <FiltroDeLista
                rotulo="Atendentes"
                opcoes={usuarios}
                selecionados={filtroUsuarios}
                onChange={setFiltroUsuarios}
              />
            )}
          />
        </div>
      )}

      {/* Barra de ferramentas: cada ícone age sobre a lista inteira. */}
      <div className="flex shrink-0 items-center gap-0.5 px-3 pb-1.5">
        <Can
          role={user.profile}
          perform="tickets-manager:showall"
          yes={() => (
            <FerramentaDaLista
              icone={showAllTickets ? Eye : EyeOff}
              rotulo="Todos"
              ativo={showAllTickets}
              onClick={() => setShowAllTickets((v) => !v)}
            />
          )}
        />

        <FerramentaDaLista
          icone={Plus}
          rotulo="Novo"
          onClick={() => setNewTicketModalOpen(true)}
        />

        <FerramentaDaLista
          icone={ListChecks}
          rotulo="Resolver todos"
          classe="text-emerald-600 dark:text-emerald-400"
          onClick={() => setConfirmarFecharTodos(true)}
        />

        <FerramentaDaLista
          icone={Inbox}
          rotulo="Abertos"
          ativo={painel === "abertos"}
          onClick={() => setPainel("abertos")}
        />

        <FerramentaDaLista
          icone={CheckSquare}
          rotulo="Fechados"
          ativo={painel === "fechados"}
          onClick={() => setPainel("fechados")}
        />

        <FerramentaDaLista
          icone={ordemAZ ? ArrowDownAZ : ArrowDownWideNarrow}
          rotulo={ordemAZ ? "Crescente" : "Decrescente"}
          ativo={ordemAZ}
          onClick={() => setOrdemAZ((v) => !v)}
        />

        <div className="ml-auto">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                Filas
                {selectedQueueIds.length > 0 && (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-semibold tabular-nums">
                    {selectedQueueIds.length}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-3">
              <p className="mb-1.5 text-xs font-medium">Mostrar filas</p>
              <TicketsQueueSelect
                className="w-full"
                selectedQueueIds={selectedQueueIds}
                userQueues={user?.queues}
                onChange={(values) => setSelectedQueueIds(values)}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Abas: ícone com a contagem em cima e o nome ao lado. Só existem no
          painel de abertos — resolvidos é o outro painel. */}
      <div
        className={cn(
          "shrink-0 border-b px-2",
          painel !== "abertos" && "hidden",
        )}
      >
        <div
          role="tablist"
          aria-label="Situação do atendimento"
          className="flex"
        >
          {abas.map((item) => {
            const ativo = estado === item.value;
            const contagem = contagens[item.value];
            const Icone = item.icone;

            return (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={ativo}
                title={item.ajuda}
                onClick={() => setEstado(item.value)}
                className={cn(
                  "relative flex flex-1 items-center justify-center gap-2 px-2 py-2.5 text-[11px] font-semibold uppercase tracking-wide transition-colors",
                  ativo
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <span className="relative">
                  <Icone className="h-[18px] w-[18px]" />
                  {contagem > 0 && (
                    <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold tabular-nums text-primary-foreground">
                      {contagem}
                    </span>
                  )}
                </span>
                <span className="truncate">{item.rotulo}</span>

                {ativo && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <ConfirmationModal
        title="Encerrar todos os atendimentos?"
        open={confirmarFecharTodos}
        onClose={() => setConfirmarFecharTodos(false)}
        onConfirm={fecharTodos}
      >
        Todas as conversas em andamento que você enxerga passam para Resolvidos.
        As mensagens continuam salvas.
      </ConfirmationModal>

      {/* Lista */}
      <div className="relative min-h-0 flex-1 border-t">
        {buscando ? (
          <TicketsList
            searchParam={searchParam}
            showAll={true}
            selectedQueueIds={selectedQueueIds}
            vazio={{
              titulo: "Nenhum resultado",
              texto: "Tente outro nome, número ou trecho da conversa.",
            }}
          />
        ) : (
          (painel === "abertos" ? abas : [RESOLVIDOS]).map((item) => (
            <div
              key={item.value}
              className={cn(
                "absolute inset-0",
                painel === "abertos" && estado !== item.value && "invisible",
              )}
            >
              <TicketsList
                // Grupo não é "aceito" por ninguém: fica em aguardando para
                // sempre. A aba lista as duas situações, senão nunca mostra
                // nada.
                status={item.somenteGrupos ? "open,pending" : item.value}
                showAll={item.value === "closed" ? true : showAllTickets}
                ordemAZ={ordemAZ}
                tagIds={filtroTags}
                userIds={filtroUsuarios}
                whatsappIds={filtroConexoes}
                selectedQueueIds={selectedQueueIds}
                isGroup={item.somenteGrupos ? "true" : "false"}
                vazio={item.vazio}
                updateCount={
                  item.value === "closed"
                    ? undefined
                    : atualizarContagem(item.value)
                }
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TicketsManager;
